import React, { createContext, useContext, useReducer, useEffect } from 'react';

const AppContext = createContext();

const parseMessageTimestamp = (message) => {
  if (!message) return 0;
  
  // Приоритет: created_at > timestamp > createdAt
  const value = message.created_at || message.timestamp || message.createdAt;
  if (!value) {
    console.warn('⚠️ [PARSE] No timestamp found for message:', message.id);
    return Number(message.id) || 0;
  }
  
  let date;
  try {
    // Если строка в формате ISO без Z, добавляем Z
    if (typeof value === 'string') {
      if (value.includes('T') && !value.includes('Z') && !value.includes('+') && !value.includes('-', 10)) {
        date = new Date(value + 'Z');
      } else {
        date = new Date(value);
      }
    } else {
      date = new Date(value);
    }
    
    const time = date.getTime();
    if (!Number.isFinite(time) || isNaN(time)) {
      console.warn('⚠️ [PARSE] Invalid timestamp for message:', message.id, 'value:', value, 'parsed:', time);
      return Number(message.id) || 0;
    }
    
    return time;
  } catch (error) {
    console.error('❌ [PARSE] Error parsing timestamp for message:', message.id, 'value:', value, 'error:', error);
    return Number(message.id) || 0;
  }
};

const sortMessagesChronologically = (messages = []) => {
  if (!messages || messages.length === 0) return [];
  
  // Логируем входные данные для отладки (только если есть запланированные сообщения или больше 2 сообщений)
  const hasScheduled = messages.some(msg => msg.isScheduled || msg.scheduledFor);
  if ((hasScheduled || messages.length > 2) && messages.length > 1) {
    console.log('🔍 [SORT] Сортировка сообщений (входные):', messages.map(msg => ({
      id: msg.id,
      created_at: msg.created_at,
      timestamp: msg.timestamp,
      createdAt: msg.createdAt,
      isScheduled: msg.isScheduled,
      scheduledFor: msg.scheduledFor,
      parsed: parseMessageTimestamp(msg),
      parsedDate: new Date(parseMessageTimestamp(msg)).toISOString()
    })));
  }
  
  const sorted = [...messages].sort((a, b) => {
    const timeA = parseMessageTimestamp(a);
    const timeB = parseMessageTimestamp(b);
    const diff = timeA - timeB;
    
    // Если времена равны или невалидны, используем ID для сортировки
    if (diff === 0 || !Number.isFinite(diff)) {
      const idDiff = (Number(a?.id) || 0) - (Number(b?.id) || 0);
      if (hasScheduled || messages.length > 2) {
        console.warn('⚠️ [SORT] Используем ID для сортировки:', {
          a: { id: a?.id, time: timeA, created_at: a?.created_at },
          b: { id: b?.id, time: timeB, created_at: b?.created_at },
          diff,
          idDiff
        });
      }
      return idDiff;
    }
    
    return diff;
  });
  
  // Логируем результат сортировки (только если есть запланированные сообщения или больше 2 сообщений)
  if ((hasScheduled || messages.length > 2) && sorted.length > 1) {
    console.log('✅ [SORT] Отсортированные сообщения (результат):', sorted.map(msg => ({
      id: msg.id,
      created_at: msg.created_at,
      parsed: parseMessageTimestamp(msg),
      parsedDate: new Date(parseMessageTimestamp(msg)).toISOString()
    })));
    
    // Проверяем правильность сортировки
    for (let i = 1; i < sorted.length; i++) {
      const prevTime = parseMessageTimestamp(sorted[i - 1]);
      const currTime = parseMessageTimestamp(sorted[i]);
      if (currTime < prevTime) {
        console.error('❌ [SORT] ОШИБКА СОРТИРОВКИ! Сообщение', sorted[i].id, 'идет после', sorted[i - 1].id, 'но имеет меньшее время:', {
          prev: { id: sorted[i - 1].id, time: prevTime, created_at: sorted[i - 1].created_at },
          curr: { id: sorted[i].id, time: currTime, created_at: sorted[i].created_at }
        });
      }
    }
  }
  
  return sorted;
};

const initialState = {
  user: null,
  chats: [],
  currentChat: null,
  messages: [],
  onlineUsers: [],
  isAuthenticated: false,
  loading: false,
  error: null,
  typingUsers: [],
  replyToMessage: null,
  showEmojiPicker: false,
  filePreview: null,
  selectedUsers: [],
  chatParticipants: [],
  notifications: [],
  lastPrivateTarget: null,
  modals: {
    createChat: false,
    userSelection: false,
    likes: false,
    chatInfo: false,
    filePreview: false,
    settings: false,
    employeesList: false,
    chatsList: false,
    shareMessage: false
  },
  shareMessageData: null,
  unreadNews: 0,
  unreadChats: 0, // глобальный счетчик непрочитанных чатов
  unreadByUser: {}, // { [userId]: count } для приватных 1:1
  pinnedMessages: {}, // { [chatId]: [messages] } для закрепленных сообщений
  voiceCall: null, // { status: 'incoming'|'outgoing'|'active', fromUser, toUser } или null
  missedCallsByUser: (() => {
    try { return JSON.parse(localStorage.getItem('missedCallsByUser') || '{}'); } catch { return {}; }
  })(),
  lastUnreadChatByUser: {}, // { [userId]: chatId } — для перехода в нужный чат из уведомлений
  lastUnreadPreviewByUser: {} // { [userId]: text } — превью первого непрочитанного сообщения
};

function appReducer(state, action) {
  // Глобальное логирование экшенов с троттлингом повторяющихся типов
  if (typeof window !== 'undefined') {
    window.__APP_STATE__ = state;
    window.__LAST_ACTION__ = action;
    const nowTs = Date.now();
    const key = action.type;
    const lastLogMap = (window.__REDUCER_LAST_LOG__ = window.__REDUCER_LAST_LOG__ || {});
    const last = lastLogMap[key] || 0;
    // По умолчанию троттлим часто стреляющие экшены (напр., SET_UNREAD_CHATS) до 1000мс
    const throttleMs = key === 'SET_UNREAD_CHATS' ? 1500 : 250;
    if (nowTs - last >= throttleMs) {
      lastLogMap[key] = nowTs;
      console.log('[REDUCER] Action:', action.type, action);
      try {
        if (typeof fetch === 'function' && key !== 'SET_UNREAD_CHATS') {
          fetch('/api/client-log', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tag: 'REDUCER_ACTION', actionType: action.type, action })
          }).catch(() => {});
        }
      } catch (_) {}
    }
  }
  switch (action.type) {
    case 'UPDATE_USER_AVATAR': {
      // Обновляем avatarUrl в localStorage
      const user = localStorage.getItem('user');
      let updatedUser = state.user;
      if (user) {
        try {
          updatedUser = { ...JSON.parse(user), avatarUrl: action.payload };
          localStorage.setItem('user', JSON.stringify(updatedUser));
        } catch (e) {
          // fallback если localStorage поврежден
          updatedUser = { ...state.user, avatarUrl: action.payload };
        }
      } else {
        updatedUser = { ...state.user, avatarUrl: action.payload };
      }
      return {
        ...state,
        user: updatedUser
      };
    }
    case 'DELETE_CHAT': {
      const chatIdToDelete = Number(action.payload.chatId);
      console.log('🗑️ [REDUCER] DELETE_CHAT вызван:', { 
        chatIdToDelete, 
        currentChatsCount: state.chats.length,
        currentChats: state.chats.map(c => ({ id: c.id, name: c.name }))
      });
      
      const filteredChats = state.chats.filter(chat => {
        const chatId = Number(chat.id);
        const shouldKeep = chatId !== chatIdToDelete;
        if (!shouldKeep) {
          console.log('🗑️ [REDUCER] Удаляем чат:', { id: chat.id, name: chat.name });
        }
        return shouldKeep;
      });
      
      console.log('✅ [REDUCER] После фильтрации осталось чатов:', filteredChats.length);
      
      let newCurrentChat = state.currentChat;
      if (state.currentChat && Number(state.currentChat.id) === chatIdToDelete) {
        console.log('🔄 [REDUCER] Сбрасываем текущий чат');
        newCurrentChat = null;
      }
      
      return {
        ...state,
        chats: filteredChats,
        currentChat: newCurrentChat,
        messages: [],
        notifications: [
          ...state.notifications,
          {
            id: Date.now(),
            type: 'info',
            message: 'Чат успешно удалён',
            timestamp: new Date().toISOString()
          }
        ]
      };
    }
    case 'SET_USER':
      return { 
        ...state, 
        user: action.payload, 
        isAuthenticated: true,
        loading: false,
        error: null
      };

    case 'SET_USER_DEPARTMENTS_ACCESS':
      if (!state.user) return state;
      return {
        ...state,
        user: {
          ...state.user,
          departments_access: Array.isArray(action.payload) ? action.payload : []
        }
      };


    case 'SET_CHATS': {
      const unreadChats = action.payload.filter(chat => chat.unread_count > 0).length;
      return { ...state, chats: action.payload, unreadChats };
    }


    case 'UPDATE_CHATS': {
      const unreadChats = action.payload.filter(chat => chat.unread_count > 0).length;
      return { ...state, chats: action.payload, unreadChats };
    }

    case 'SET_CURRENT_CHAT': {
        const current = action.payload;
        let nextUnreadByUser = state.unreadByUser || {};
        let nextMissedCallsByUser = state.missedCallsByUser || {};
        if (current && current.isPrivate && Array.isArray(current.participants)) {
          const meId = state.user?.id;
          const other = current.participants.find(p => p.id !== meId);
          if (other && other.id != null) {
            nextUnreadByUser = { ...nextUnreadByUser, [other.id]: 0 };
            nextMissedCallsByUser = { ...nextMissedCallsByUser, [other.id]: 0 };
            try {
              localStorage.setItem('missedCallsByUser', JSON.stringify(nextMissedCallsByUser));
            } catch (_) {}
          }
        }
        if (action.payload) {
          localStorage.setItem('currentChat', JSON.stringify(action.payload));
        } else {
          localStorage.removeItem('currentChat');
        }
        return {
        ...state,
        currentChat: action.payload,
        replyToMessage: null,
        showEmojiPicker: false,
        unreadByUser: nextUnreadByUser,
        missedCallsByUser: nextMissedCallsByUser
      };
    }

    case 'SET_LAST_PRIVATE_TARGET':
      return { ...state, lastPrivateTarget: action.payload };

    case 'CLEAR_LAST_PRIVATE_TARGET':
      return { ...state, lastPrivateTarget: null };

    case 'SET_MESSAGES': {
      // Поддержка формата { chatId, messages } — полная замена истории для текущего чата
      const isPayloadForChat = action.payload && typeof action.payload === 'object' && 'chatId' in action.payload && 'messages' in action.payload;
      const incoming = isPayloadForChat
        ? (Array.isArray(action.payload.messages) ? action.payload.messages : [])
        : (Array.isArray(action.payload) ? action.payload : []);
      const replaceForCurrentChat = isPayloadForChat && action.payload.chatId === state.currentChat?.id;
      const shouldReplace = replaceForCurrentChat || !state.messages || state.messages.length === 0 || incoming.length === 0;
      if (shouldReplace) {
        return { ...state, messages: sortMessagesChronologically(incoming) };
      }
      // Иначе МЕРДЖИМ: существующие + новые, без потери старых сообщений
      const byId = new Map();
      for (const msg of state.messages) {
        if (msg && msg.id != null) byId.set(msg.id, msg);
      }
      for (const msg of incoming) {
        if (msg && msg.id != null) {
          const prev = byId.get(msg.id) || {};
          byId.set(msg.id, { ...prev, ...msg });
        }
      }
      const merged = Array.from(byId.values());
      return { ...state, messages: sortMessagesChronologically(merged) };
    }

    case 'ADD_MESSAGE':
      if (action.payload.chat_id === state.currentChat?.id) {
        // Проверяем, нет ли уже такого сообщения в списке (по ID)
        const existingIndex = state.messages.findIndex(msg => msg.id === action.payload.id);
        if (existingIndex >= 0) {
          // Если сообщение уже есть, просто обновляем его данные
          const updatedMessages = [...state.messages];
          updatedMessages[existingIndex] = { ...updatedMessages[existingIndex], ...action.payload };
          return {
            ...state,
            messages: updatedMessages
          };
        }
        // Если сообщения нет, ДОБАВЛЯЕМ ЕГО В КОНЕЦ БЕЗ ПЕРЕСОРТИРОВКИ
        // (порядок сообщений теперь полностью определяется сервером)
        return {
          ...state,
          messages: [...state.messages, action.payload]
        };
      }
      return state;

    case 'UPDATE_MESSAGE':
      return {
        ...state,
        messages: state.messages.map(msg =>
          msg.id === action.payload.id ? { ...msg, ...action.payload } : msg
        )
      };

    case 'DELETE_MESSAGE':
      console.log('[REDUCER] DELETE_MESSAGE', action.payload.messageId, state.messages.map(m => m.id));
      try {
        if (typeof fetch === 'function') {
          fetch('/api/client-log', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tag: 'DELETE_MESSAGE',
              data: {
                messageId: action.payload.messageId,
                messages: state.messages.map(m => m.id)
              }
            })
          }).catch(() => {});
        }
      } catch (_) {}
      return {
        ...state,
        messages: state.messages.filter(msg => String(msg.id) !== String(action.payload.messageId))
      };

    case 'UPDATE_MESSAGE_LIKES':
      return {
        ...state,
        messages: state.messages.map(msg =>
          msg.id === action.payload.messageId
            ? { 
                ...msg, 
                likes_count: action.payload.likesCount, 
                user_liked: action.payload.userLiked ? 1 : 0
              }
            : msg
        )
      };

    case 'SET_ONLINE_USERS':
      return { ...state, onlineUsers: action.payload };

    case 'SET_TYPING_USERS':
      return { ...state, typingUsers: action.payload };

    case 'ADD_TYPING_USER':
      if (!state.typingUsers.includes(action.payload)) {
        return { 
          ...state, 
          typingUsers: [...state.typingUsers, action.payload] 
        };
      }
      return state;

    case 'REMOVE_TYPING_USER':
      return { 
        ...state, 
        typingUsers: state.typingUsers.filter(user => user !== action.payload) 
      };

    case 'SET_REPLY_TO_MESSAGE':
      return { 
        ...state, 
        replyToMessage: action.payload,
        showEmojiPicker: false
      };

    case 'SET_FILE_PREVIEW':
      return { ...state, filePreview: action.payload };

    case 'TOGGLE_EMOJI_PICKER':
      return { 
        ...state, 
        showEmojiPicker: !state.showEmojiPicker
      };

    case 'CLOSE_EMOJI_PICKER':
      return { ...state, showEmojiPicker: false };

    case 'TOGGLE_MODAL':
      const newModals = { ...state.modals };
      
      if (action.payload.show) {
        Object.keys(newModals).forEach(key => {
          newModals[key] = key === action.payload.modal;
        });
      } else {
        newModals[action.payload.modal] = false;
      }

      return {
        ...state,
        modals: newModals,
        ...(action.payload.modal === 'userSelection' && !action.payload.show && {
          selectedUsers: []
        }),
        ...(action.payload.modal === 'chatInfo' && !action.payload.show && {
          chatParticipants: []
        }),
        ...(action.payload.modal === 'shareMessage' && !action.payload.show && {
          shareMessageData: null
        })
      };

    case 'SHOW_SHARE_MODAL':
      return {
        ...state,
        modals: {
          ...Object.keys(state.modals).reduce((acc, key) => {
            acc[key] = false;
            return acc;
          }, {}),
          shareMessage: true
        },
        shareMessageData: action.payload.message
      };

    case 'CLOSE_ALL_MODALS':
      return {
        ...state,
        modals: Object.keys(state.modals).reduce((acc, key) => {
          acc[key] = false;
          return acc;
        }, {}),
        selectedUsers: [],
        chatParticipants: []
      };

    case 'SET_SELECTED_USERS':
      return { ...state, selectedUsers: action.payload };

    case 'ADD_SELECTED_USER':
      if (!state.selectedUsers.find(user => user.id === action.payload.id)) {
        return { 
          ...state, 
          selectedUsers: [...state.selectedUsers, action.payload] 
        };
      }
      return state;

    case 'REMOVE_SELECTED_USER':
      return { 
        ...state, 
        selectedUsers: state.selectedUsers.filter(user => user.id !== action.payload.id) 
      };

    case 'SET_CHAT_PARTICIPANTS':
      return { ...state, chatParticipants: action.payload };

    case 'ADD_NOTIFICATION':
      return { 
        ...state, 
        notifications: [...state.notifications, {
          id: Date.now(),
          ...action.payload,
          timestamp: new Date().toISOString()
        }] 
      };

    case 'REMOVE_NOTIFICATION':
      return { 
        ...state, 
        notifications: state.notifications.filter(notif => notif.id !== action.payload.id) 
      };

    case 'CLEAR_NOTIFICATIONS':
      return { ...state, notifications: [] };

    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SET_ERROR':
      return { 
        ...state, 
        error: action.payload,
        loading: false
      };

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    case 'UPDATE_USER_STATUS':
      return {
        ...state,
        onlineUsers: state.onlineUsers.map(user =>
          user.id === action.payload.userId
            ? { ...user, online: action.payload.online }
            : user
        )
      };


    case 'UPDATE_CHAT_UNREAD_COUNT': {
      const { chatId, count = 1, mode = 'increment' } = action.payload || {};
      const updatedChats = state.chats.map(chat => {
        if (chat.id !== chatId) return chat;
        const current = Number(chat.unread_count) || 0;
        const next = mode === 'set' ? Math.max(0, Number(count) || 0) : current + (Number(count) || 1);
        return { ...chat, unread_count: next };
      });
      const unreadChats = updatedChats.filter(chat => (Number(chat.unread_count) || 0) > 0).length;
      return {
        ...state,
        chats: updatedChats,
        unreadChats
      };
    }


    case 'MARK_CHAT_AS_READ': {
      const updatedChats = state.chats.map(chat =>
        chat.id === action.payload.chatId
          ? { ...chat, unread_count: 0 }
          : chat
      );
      const unreadChats = updatedChats.filter(chat => chat.unread_count > 0).length;
      let nextUnreadByUser = { ...(state.unreadByUser || {}) };
      let nextMissedCallsByUser = { ...(state.missedCallsByUser || {}) };
      const participants = action.payload.participants;
      if (Array.isArray(participants)) {
        participants.forEach(p => {
          if (p && p.id != null) {
            nextUnreadByUser[p.id] = 0;
            nextMissedCallsByUser[p.id] = 0;
          }
        });
      }
      return {
        ...state,
        chats: updatedChats,
        unreadChats,
        unreadByUser: nextUnreadByUser,
        missedCallsByUser: nextMissedCallsByUser
      };
    }

    case 'UPDATE_LAST_MESSAGE':
      return {
        ...state,
        chats: state.chats.map(chat =>
          chat.id === action.payload.chatId
            ? { 
                ...chat, 
                last_message: action.payload.content,
                last_message_time: action.payload.timestamp
              }
            : chat
        )
      };

    case 'INCREMENT_UNREAD_BY_USER': {
      const userId = action.payload?.userId;
      if (userId == null) return state;
      const current = Number((state.unreadByUser || {})[userId]) || 0;
      const nextCount = current + 1;
      const hasPreview = (state.lastUnreadPreviewByUser || {})[userId];
      const previewText = action.payload?.text;
      return {
        ...state,
        unreadByUser: { ...(state.unreadByUser || {}), [userId]: nextCount },
        lastUnreadChatByUser: action.payload?.chatId != null
          ? { ...(state.lastUnreadChatByUser || {}), [userId]: action.payload.chatId }
          : (state.lastUnreadChatByUser || {}),
        lastUnreadPreviewByUser: !hasPreview && previewText
          ? { ...(state.lastUnreadPreviewByUser || {}), [userId]: previewText }
          : (state.lastUnreadPreviewByUser || {})
      };
    }

    case 'CLEAR_UNREAD_BY_USER': {
      const userId = action.payload?.userId;
      if (userId == null) return state;
      const nextLast = { ...(state.lastUnreadChatByUser || {}) };
      delete nextLast[userId];
      const nextPreview = { ...(state.lastUnreadPreviewByUser || {}) };
      delete nextPreview[userId];
      return {
        ...state,
        unreadByUser: { ...(state.unreadByUser || {}), [userId]: 0 },
        lastUnreadChatByUser: nextLast,
        lastUnreadPreviewByUser: nextPreview
      };
    }

    case 'LOGOUT':
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('currentChat');
      return { ...initialState };

    case 'RESET_STATE':
      return { ...initialState };

    case 'INCREMENT_UNREAD_NEWS':
      return { ...state, unreadNews: state.unreadNews + 1 };

    case 'RESET_UNREAD_NEWS':
      return { ...state, unreadNews: 0 };

    case 'SET_UNREAD_NEWS_COUNT':
      return { ...state, unreadNews: action.payload };

    case 'SET_PINNED_MESSAGES':
      return { 
        ...state, 
        pinnedMessages: {
          ...state.pinnedMessages,
          [action.payload.chatId]: action.payload.messages
        }
      };

    case 'ADD_PINNED_MESSAGE':
      return {
        ...state,
        pinnedMessages: {
          ...state.pinnedMessages,
          [action.payload.chatId]: [
            ...(state.pinnedMessages[action.payload.chatId] || []),
            action.payload.message
          ]
        }
      };

    case 'REMOVE_PINNED_MESSAGE':
      return {
        ...state,
        pinnedMessages: {
          ...state.pinnedMessages,
          [action.payload.chatId]: (state.pinnedMessages[action.payload.chatId] || [])
            .filter(msg => msg.id !== action.payload.messageId)
        }
      };

    case 'UPDATE_MESSAGE_PIN_STATUS':
      return {
        ...state,
        messages: state.messages.map(msg => 
          msg.id === action.payload.messageId 
            ? { ...msg, is_pinned: action.payload.isPinned }
            : msg
        )
      };

    case 'SET_VOICE_CALL':
      return { ...state, voiceCall: action.payload };

    case 'CLEAR_VOICE_CALL': {
      const payload = action.payload;
      const fromUserId = payload?.fromUserId;
      const wasIncoming = state.voiceCall?.status === 'incoming' && state.voiceCall?.fromUser?.id != null;
      const shouldAddMissed = fromUserId != null && wasIncoming && Number(state.voiceCall.fromUser.id) === Number(fromUserId);
      let nextMissed = state.missedCallsByUser || {};
      if (shouldAddMissed) {
        const cur = Number(nextMissed[fromUserId]) || 0;
        nextMissed = { ...nextMissed, [fromUserId]: cur + 1 };
        try {
          localStorage.setItem('missedCallsByUser', JSON.stringify(nextMissed));
        } catch (_) {}
      }
      return { ...state, voiceCall: null, missedCallsByUser: nextMissed };
    }

    case 'RESET_MISSED_CALLS':
      if (action.payload?.userId == null) return state;
      const uid = action.payload.userId;
      const next = { ...(state.missedCallsByUser || {}), [uid]: 0 };
      try {
        localStorage.setItem('missedCallsByUser', JSON.stringify(next));
      } catch (_) {}
      return { ...state, missedCallsByUser: next };

    default:
      return state;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Инициализация пользователя из localStorage
  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    if (token && user) {
      try {
        const parsedUser = JSON.parse(user);
        dispatch({ 
          type: 'SET_USER', 
          payload: { ...parsedUser, token } 
        });
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
      }
    }
    
    // Восстанавливаем текущий чат из localStorage
    const savedCurrentChat = localStorage.getItem('currentChat');
    if (savedCurrentChat) {
      try {
        const parsedCurrentChat = JSON.parse(savedCurrentChat);
        dispatch({ 
          type: 'SET_CURRENT_CHAT', 
          payload: parsedCurrentChat 
        });
        
        // НЕ запрашиваем закрепленные сообщения сразу - это будет сделано в SocketProvider
        // после проверки доступа к чату
        console.log('[AppContext] Восстановлен чат из localStorage:', parsedCurrentChat.id);
        // SocketProvider проверит доступ к чату и запросит данные, если доступ есть
        
      } catch (error) {
        console.error('Error parsing currentChat data:', error);
        localStorage.removeItem('currentChat');
      }
    }
    // Подписка на событие истории сообщений чата
    if (window.socket) {
      const onChatMessages = ({ chatId, messages }) => {
        console.log('[CLIENT][chat_messages] получено событие', { chatId, messagesCount: messages?.length });
        fetch && fetch('/api/client-log', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tag: 'CLIENT_CHAT_MESSAGES', data: { chatId, messagesCount: messages?.length, messages } })
        });
        dispatch({ type: 'SET_MESSAGES', payload: { chatId, messages: messages || [] } });
      };
      window.socket.on('chat_messages', onChatMessages);
      return () => {
        window.socket.off('chat_messages', onChatMessages);
      };
    }
  }, []);

  // Автоматическое удаление уведомлений через 5 секунд
  useEffect(() => {
    state.notifications.forEach(notification => {
      if (!notification.persistent) {
        setTimeout(() => {
          dispatch({
            type: 'REMOVE_NOTIFICATION',
            payload: { id: notification.id }
          });
        }, 5000);
      }
    });
  }, [state.notifications]);

  // Закрытие панели эмодзи при клике вне её
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (state.showEmojiPicker && 
          !event.target.closest('.emoji-picker') && 
          !event.target.closest('.emoji-btn')) {
        dispatch({ type: 'CLOSE_EMOJI_PICKER' });
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [state.showEmojiPicker]);

  const value = {
    state,
    dispatch
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};